import { useRef, useState, useEffect } from 'react'

// 国家代码到中文名称的映射
const countryNameMap = {
  US: '美国',
  CA: '加拿大',
  DE: '德国',
  FR: '法国',
  GB: '英国',
  ES: '西班牙',
  IT: '意大利',
  NL: '荷兰',
  BE: '比利时',
  CH: '瑞士',
  SE: '瑞典',
  PL: '波兰',
  CN: '中国大陆',
  HK: '香港',
  MO: '澳门',
  TW: '台湾',
  JP: '日本',
  KR: '韩国',
  SG: '新加坡',
  AE: '阿联酋',
  SA: '沙特阿拉伯',
  EG: '埃及',
  AU: '澳大利亚',
  NZ: '新西兰',
  SN: '塞内加尔',
  CV: '佛得角',
  VN: '越南',
  KH: '柬埔寨',
  MN: '蒙古'
}

// 已开通国家列表（使用 SVG 国家代码）
const activeCountries = new Set([
  'US', 'CA',
  'DE', 'FR', 'GB', 'ES', 'IT', 'NL', 'BE', 'CH', 'SE', 'PL',
  'CN', 'HK', 'MO', 'JP', 'KR', 'SG', 'AE', 'SA', 'EG',
  'AU', 'NZ',
  'SN', 'CV',
  'VN', 'KH', 'MN'
])

// 已开通国家颜色配置
// 默认所有国家使用统一的浅色，hover 时颜色加深，重点地区 hover 时更深（金褐色）
const DEFAULT_COUNTRY_COLOR = { fill: '#f5ebd8', stroke: '#c9a961' }
const HOVER_COUNTRY_COLOR = { fill: '#e8d5b5', stroke: '#b89560' }  // 普通地区 hover
const HOVER_PRIORITY_COLOR = { fill: '#c9a961', stroke: '#9a7a4a' }  // 重点地区 hover（金褐色）

// 重点地区名单（hover 时颜色更深）
const priorityRegions = new Set([
  'US', 'CA',  // North America
  'GB', 'DE',  // Europe
  'KH', 'VN',  // Southeast Asia
  'MN',        // Central Asia
  'SN', 'CV',  // Africa
  'KR', 'JP',  // East Asia
  'CN', 'HK', 'MO'  // China & Special Administrative Regions
])

// 需要点亮的地区（非国家，但需要显示为已开通颜色）- 政治正确考虑
const highlightedRegions = new Set([
  'TW'  // 台湾 - 中国领土
])

// 国家代码到大洲的映射
const countryContinentMap = {
  US: 'northAmerica', CA: 'northAmerica',
  DE: 'europe', FR: 'europe', GB: 'europe', ES: 'europe', IT: 'europe',
  NL: 'europe', BE: 'europe', CH: 'europe', SE: 'europe', PL: 'europe',
  CN: 'asia', HK: 'asia', MO: 'asia', TW: 'asia', JP: 'asia', KR: 'asia',
  SG: 'asia', AE: 'asia', SA: 'asia', VN: 'asia', KH: 'asia', MN: 'asia',
  EG: 'africa',  // 埃及属于非洲
  AU: 'oceania', NZ: 'oceania',
  SN: 'africa', CV: 'africa'
}

// class 名称到国家代码的映射
const classToCountryCode = {
  'United States': 'US',
  'United Kingdom': 'GB',
  'China': 'CN',
  'France': 'FR',
  'Spain': 'ES',
  'Italy': 'IT',
  'Netherlands': 'NL',
  'Switzerland': 'CH',
  'Sweden': 'SE',
  'Poland': 'PL',
  'Japan': 'JP',
  'South Korea': 'KR',
  'Australia': 'AU',
  'New Zealand': 'NZ',
  'United Arab Emirates': 'AE',
  'Saudi Arabia': 'SA',
  'Egypt': 'EG',
  'Singapore': 'SG',
  'Canada': 'CA',
  'Germany': 'DE',
  'Belgium': 'BE',
  'Senegal': 'SN',
  'Cape Verde': 'CV',
  'Vietnam': 'VN',
  'Cambodia': 'KH',
  'Mongolia': 'MN',
  'Taiwan': 'TW'
}

// 预设国家中心点坐标（所有已开通国家都使用预设坐标确保准确性）
// 坐标基于 SVG viewBox 0 0 2000 857，使用边界框中心计算
const presetCenters = {
  // 北美洲 - 使用边界框中心（因为包含阿拉斯加等海外领土）
  US: { x: 373, y: 219 },      // 美国（包含本土和阿拉斯加）
  CA: { x: 563, y: 117 },      // 加拿大

  // 欧洲
  DE: { x: 1038, y: 175 },     // 德国
  FR: { x: 1000, y: 206 },     // 法国
  GB: { x: 972, y: 156 },      // 英国
  ES: { x: 970, y: 246 },      // 西班牙
  IT: { x: 1052, y: 233 },     // 意大利
  NL: { x: 1020, y: 180 },     // 荷兰
  BE: { x: 1015, y: 185 },     // 比利时
  CH: { x: 1035, y: 205 },     // 瑞士
  SE: { x: 1063, y: 110 },     // 瑞典（边界框中心）
  PL: { x: 1079, y: 170 },     // 波兰（边界框中心）

  // 亚洲
  CN: { x: 1517, y: 273 },     // 中国大陆
  HK: { x: 1632, y: 360 },     // 香港（中国东南沿海，+75, -15）
  MO: { x: 1618, y: 358 },     // 澳门（香港西侧，+75, -15）
  TW: { x: 1655, y: 350 },     // 台湾（中国东南）
  JP: { x: 1709, y: 256 },     // 日本
  KR: { x: 1665, y: 265 },     // 韩国
  SG: { x: 1573, y: 483 },     // 新加坡（马来半岛南端，+85）
  VN: { x: 1577, y: 399 },     // 越南（中南半岛）
  KH: { x: 1577, y: 421 },     // 柬埔寨（中南半岛）
  MN: { x: 1504, y: 202 },     // 蒙古（中亚）
  AE: { x: 1295, y: 340 },     // 阿联酋
  SA: { x: 1240, y: 330 },     // 沙特阿拉伯

  // 大洋洲
  AU: { x: 1716, y: 676 },     // 澳大利亚
  NZ: { x: 1878, y: 762 },     // 新西兰

  // 非洲
  SN: { x: 915, y: 410 },      // 塞内加尔
  CV: { x: 852, y: 399 },      // 佛得角
  EG: { x: 1140, y: 340 }      // 埃及（非洲东北部，+80, -40）
}

// 计算路径的中心点（使用边界框中心）
const calculatePathCenter = (d) => {
  const numbers = d.match(/[\d.]+/g)
  if (!numbers || numbers.length < 2) return null

  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity
  let sumX = 0, sumY = 0, count = 0

  for (let i = 0; i < numbers.length; i += 2) {
    const x = parseFloat(numbers[i])
    const y = parseFloat(numbers[i + 1])
    if (isNaN(x) || isNaN(y)) continue

    minX = Math.min(minX, x)
    maxX = Math.max(maxX, x)
    minY = Math.min(minY, y)
    maxY = Math.max(maxY, y)
    sumX += x
    sumY += y
    count++
  }

  if (count === 0) return null

  // 使用边界框中心
  return {
    x: (minX + maxX) / 2,
    y: (minY + maxY) / 2,
    size: (maxX - minX) * (maxY - minY),
    minY: minY,
    maxY: maxY
  }
}

// 合并多个路径的中心点（加权平均）
const mergeCenters = (centers) => {
  if (centers.length === 0) return null
  if (centers.length === 1) return {
    x: centers[0].x,
    y: centers[0].y,
    minY: centers[0].minY,
    maxY: centers[0].maxY
  }

  let sumX = 0, sumY = 0, totalWeight = 0
  let minOverallY = Infinity, maxOverallY = -Infinity

  for (let i = 0; i < centers.length; i++) {
    const w = centers[i].size || 1
    sumX += centers[i].x * w
    sumY += centers[i].y * w
    totalWeight += w
    minOverallY = Math.min(minOverallY, centers[i].minY)
    maxOverallY = Math.max(maxOverallY, centers[i].maxY)
  }

  return {
    x: sumX / totalWeight,
    y: sumY / totalWeight,
    minY: minOverallY,
    maxY: maxOverallY
  }
}

export default function WorldMap({ activeContinent, hoveredMarker, setHoveredMarker, markerCoordinates, getCountryName }) {
  const containerRef = useRef(null)
  const [svgContent, setSvgContent] = useState(null)
  const [countryData, setCountryData] = useState({})

  // 加载 SVG 内容并计算国家中心点
  useEffect(() => {
    fetch('/world-map.svg')
      .then(res => res.text())
      .then(content => {
        setSvgContent(content)

        // 解析所有国家路径并计算中心点
        const paths = content.match(/<path[^>]*d="[^"]*"[^>]*>/g) || []
        const countryPaths = {}

        paths.forEach((pathStr) => {
          const dMatch = pathStr.match(/d="([^"]*)"/)
          const idMatch = pathStr.match(/id="([^"]*)"/)
          const classMatch = pathStr.match(/class="([^"]*)"/)

          if (!dMatch) return

          const d = dMatch[1]
          const center = calculatePathCenter(d)
          if (!center) return

          // 优先使用 id，其次使用 class
          if (idMatch && idMatch[1] && idMatch[1].length === 2) {
            const countryCode = idMatch[1]
            if (!countryPaths[countryCode]) {
              countryPaths[countryCode] = []
            }
            countryPaths[countryCode].push(center)
          } else if (classMatch && classMatch[1]) {
            const className = classMatch[1]
            const countryCode = classToCountryCode[className]
            if (countryCode) {
              if (!countryPaths[countryCode]) {
                countryPaths[countryCode] = []
              }
              countryPaths[countryCode].push(center)
            }
          }
        })

        // 计算每个国家的最终中心点
        const finalCountryData = {}

        // 首先处理 SVG 中找到的国家路径
        Object.entries(countryPaths).forEach(([code, centers]) => {
          // 优先使用预设坐标（针对小国家/地区）
          if (presetCenters[code]) {
            finalCountryData[code] = presetCenters[code]
          } else {
            const merged = mergeCenters(centers)
            if (merged) {
              finalCountryData[code] = merged
            }
          }
        })

        // 确保所有在 presetCenters 中定义的国家都被包含（即使 SVG 中没有对应路径）
        Object.keys(presetCenters).forEach((code) => {
          if (!finalCountryData[code]) {
            finalCountryData[code] = presetCenters[code]
          }
        })

        setCountryData(finalCountryData)
      })
      .catch(err => console.error('Failed to load world map:', err))
  }, [])

  // 检查国家是否属于选中的大洲
  const isCountryInSelectedContinent = (countryCode) => {
    if (activeContinent === 'all') return true
    const continent = countryContinentMap[countryCode]
    return continent === activeContinent
  }

  // 根据国家代码获取在 markerCoordinates 中的索引
  const getCountryIndex = (countryCode) => {
    const continent = countryContinentMap[countryCode]
    if (!continent || !markerCoordinates[continent]) return 0

    const codeMap = {
      US: 'united_states', CA: 'canada',
      DE: 'germany', FR: 'france', GB: 'united_kingdom',
      ES: 'spain', IT: 'italy', NL: 'netherlands',
      BE: 'belgium', CH: 'switzerland', SE: 'sweden', PL: 'poland',
      CN: 'china', HK: 'hong_kong', MO: 'macao',
      JP: 'japan', KR: 'south_korea', SG: 'singapore',
      AE: 'uae', SA: 'saudi_arabia', EG: 'egypt',
      AU: 'australia', NZ: 'new_zealand',
      SN: 'senegal', CV: 'cape_verde',
      VN: 'vietnam', KH: 'cambodia', MN: 'mongolia'
    }

    const targetKey = codeMap[countryCode]
    const index = markerCoordinates[continent].findIndex(item => item.key === targetKey)
    return index >= 0 ? index : 0
  }

  // 渲染标记点
  const renderMarkers = () => {
    return Object.entries(countryData)
      .filter(([countryCode]) => activeCountries.has(countryCode))
      .map(([countryCode, center]) => {
        const continent = countryContinentMap[countryCode]
        const isSelected = activeContinent === 'all' || activeContinent === continent
        const countryIndex = getCountryIndex(countryCode)

        return (
          <g
            key={`marker-${countryCode}`}
            className={`map-marker-group ${!isSelected ? 'marker-dimmed' : ''}`}
            transform={`translate(${center.x}, ${center.y})`}
            onMouseEnter={() => setHoveredMarker({
              continent,
              index: countryIndex,
              countryCode,
              svgX: center.x,
              svgY: center.y,
              minY: center.minY,
              maxY: center.maxY
            })}
            onMouseLeave={() => setHoveredMarker(null)}
            style={{ cursor: 'pointer' }}
          >
            <circle className="marker-pulse" r="10" fill="#c9a961" opacity="0.6">
              <animate attributeName="r" values="10;20;10" dur="2s" repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.6;0.2;0.6" dur="2s" repeatCount="indefinite" />
            </circle>
            <circle r="6" fill="#c9a961" stroke="white" strokeWidth="2" />
          </g>
        )
      })
  }

  // 渲染提示框 - 根据纬度智能定位
  const renderTooltip = () => {
    if (!hoveredMarker) return null

    const tooltipX = hoveredMarker.svgX
    // 高纬度国家（y < 200）向下显示，其他国家向上显示
    // 确保 tooltip 不会被 SVG 边界裁剪
    const isHighLatitude = hoveredMarker.svgY < 200
    const tooltipY = isHighLatitude
      ? hoveredMarker.svgY + 70  // 向下显示
      : hoveredMarker.svgY - 70  // 向上显示

    return (
      <g className="marker-tooltip-group" transform={`translate(${tooltipX}, ${tooltipY})`}>
        <rect
          x="-120"
          y="-32"
          width="240"
          height="64"
          rx="8"
          fill="#c9a961"
          opacity="0.95"
          filter="drop-shadow(0 4px 16px rgba(0, 0, 0, 0.4))"
        />
        <text
          x="0"
          y="0"
          textAnchor="middle"
          dominantBaseline="middle"
          fill="white"
          fontSize="22"
          fontWeight="600"
          fontFamily="sans-serif"
        >
          {getCountryName(hoveredMarker.continent, hoveredMarker.index)}
        </text>
      </g>
    )
  }

  // 渲染 SVG 路径
  const renderPaths = () => {
    if (!svgContent) return null

    const paths = svgContent.match(/<path[^>]*d="[^"]*"[^>]*>/g) || []

    return paths.map((pathStr, index) => {
      const dMatch = pathStr.match(/d="([^"]*)"/)
      const idMatch = pathStr.match(/id="([^"]*)"/)
      const classMatch = pathStr.match(/class="([^"]*)"/)

      if (!dMatch) return null

      // 确定国家代码
      const countryId = idMatch?.[1]
      const className = classMatch?.[1] || ''

      let countryCode = null
      if (countryId && countryId.length === 2) {
        countryCode = countryId
      } else if (className && classToCountryCode[className]) {
        countryCode = classToCountryCode[className]
      }

      // 检查国家状态
      const isActive = countryCode && activeCountries.has(countryCode)
      const isHighlighted = countryCode && highlightedRegions.has(countryCode)
      const isPriority = countryCode && priorityRegions.has(countryCode)
      const continent = countryCode ? countryContinentMap[countryCode] : null
      const isInSelectedContinent = activeContinent === 'all' ||
        (continent && activeContinent === continent)

      // 检查是否 hover 在该国家上
      const isHovered = hoveredMarker && hoveredMarker.countryCode === countryCode

      // 根据状态设置样式
      let fill, stroke, opacity
      if (!continent) {
        // 未开通的国家/地区 - 使用中等灰色，与背景区分
        fill = '#5a6a8b'
        stroke = '#4a5a7b'
        opacity = 0.7
      } else if (!isInSelectedContinent) {
        // 不在选中大洲的已开通国家
        fill = '#4a5a7b'
        stroke = '#3a4a6b'
        opacity = 0.4
      } else if (isActive || isHighlighted) {
        // 已开通国家或需要点亮的地区
        if (isHovered) {
          // hover 状态：重点地区使用更深的金褐色，普通地区使用浅色加深
          const colors = isPriority ? HOVER_PRIORITY_COLOR : HOVER_COUNTRY_COLOR
          fill = colors.fill
          stroke = colors.stroke
        } else {
          // 默认状态：所有国家使用统一的浅色
          fill = DEFAULT_COUNTRY_COLOR.fill
          stroke = DEFAULT_COUNTRY_COLOR.stroke
        }
        opacity = 1
      } else {
        // 未开通但在选中大洲的国家
        fill = '#6a7a9b'
        stroke = '#5a6a8b'
        opacity = 0.7
      }

      return (
        <path
          key={`${countryId || className || 'path'}-${index}`}
          d={dMatch[1]}
          className={className}
          fill={fill}
          stroke={stroke}
          strokeWidth="0.5"
          opacity={opacity}
        />
      )
    })
  }

  return (
    <div ref={containerRef} className="world-map-svg-container">
      {svgContent ? (
        <svg
          viewBox="0 0 2000 857"
          className="world-map-svg"
          preserveAspectRatio="xMidYMid slice"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <style>{`
              .world-map-svg {
                background: linear-gradient(135deg, #0f1c3a 0%, #1a3a6f 50%, #2d5a8f 100%);
              }
              .map-marker-group.marker-dimmed {
                opacity: 0.3;
              }
              .map-marker-group.marker-dimmed .marker-pulse {
                animation: none !important;
                fill: #666;
                opacity: 0.5;
              }
              .map-marker-group.marker-dimmed circle[r="6"] {
                fill: #666;
              }
            `}</style>
          </defs>

          {/* 渲染国家路径 */}
          <g className="world-map-paths">
            {renderPaths()}
          </g>

          {/* 标记点 */}
          <g className="map-markers-overlay">
            {renderMarkers()}
          </g>

          {/* 提示框 */}
          {renderTooltip()}
        </svg>
      ) : (
        <div className="map-loading">Loading map...</div>
      )}
    </div>
  )
}
